/**
 * NFTListingPage.js — public, shareable buy page for a teen's listed NFT.
 * Proceeds settle directly from Crossmint to the teen's wallet.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Gem, ShieldCheck, CreditCard, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NFTListingPage = () => {
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buying, setBuying] = useState(false);
  const [buyMsg, setBuyMsg] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/nft/listings/${listingId}`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Listing not found'))))
      .then(setListing)
      .catch(e => setError(e.message));
  }, [listingId]);

  const buy = async () => {
    if (!buyerEmail.trim()) return;
    setBuying(true);
    setBuyMsg(null);
    try {
      const r = await fetch(`${API}/api/nft/listings/${listingId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buyer_email: buyerEmail.trim() }),
      });
      const d = await r.json();
      if (d.available && d.checkout_url) {
        window.location.href = d.checkout_url;
      } else {
        setBuyMsg(d.reason || d.detail || 'Checkout is temporarily unavailable.');
      }
    } catch (e) {
      setBuyMsg('Checkout is temporarily unavailable — please try again shortly.');
    }
    setBuying(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="nft-listing-error">
        <p className="text-slate-300 font-semibold">{error}</p>
      </div>
    );
  }
  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="nft-listing-loading">
        <Loader2 className="animate-spin text-amber-400" size={32} />
      </div>
    );
  }

  const sold = listing.status === 'sold';
  const active = listing.status === 'listed';
  const price = listing.price_pence ? `£${(listing.price_pence / 100).toFixed(2)}` : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-10" data-testid="nft-listing-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-10 w-auto" />
          <p className="text-sm font-bold tracking-[0.2em] text-amber-300 uppercase">HiBFF NFT</p>
        </div>

        <div className="rounded-3xl overflow-hidden border border-white/10 bg-slate-900 shadow-2xl">
          <div className="relative aspect-square bg-slate-800">
            <img src={`${API}/api/memes/${listing.meme_id}/image`} alt={listing.title}
              className="w-full h-full object-cover" data-testid="nft-listing-image" />
            {sold && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="px-6 py-2 rounded-full bg-emerald-500 text-white font-black text-xl tracking-wider rotate-[-8deg] shadow-2xl" data-testid="nft-sold-badge">
                  SOLD
                </span>
              </div>
            )}
            <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow">
              <Gem size={18} className="text-white" />
            </span>
          </div>

          <div className="p-6">
            <h1 className="text-2xl font-black mb-1" data-testid="nft-listing-title">{listing.title}</h1>
            {listing.challenge && (
              <p className="text-xs text-amber-300/80 font-semibold uppercase tracking-wide mb-2">
                Earned: {listing.challenge}
              </p>
            )}
            <p className="text-sm text-slate-300 mb-4">{listing.description}</p>

            <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-800/70 border border-white/5 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">
                  {sold ? 'Sold for' : 'Price'}
                </p>
                <p className="text-3xl font-black text-amber-300" data-testid="nft-listing-price">
                  {sold && listing.sale_price_pence
                    ? `£${(listing.sale_price_pence / 100).toFixed(2)}`
                    : price || '—'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Chain</p>
                <p className="text-sm font-bold text-slate-200 capitalize">{listing.chain || 'polygon'}</p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-900/30 border border-emerald-500/20 mb-5">
              <ShieldCheck size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-200/90">
                A one-of-one achievement NFT, earned through real-world bravery on HiBFF.
                100% of the sale proceeds go directly to the young creator&apos;s own wallet.
              </p>
            </div>

            {sold ? (
              <div className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 font-bold" data-testid="nft-listing-sold-panel">
                <CheckCircle2 size={18} /> This NFT has found its home
              </div>
            ) : active ? (
              <div className="space-y-3" data-testid="nft-buy-panel">
                <input
                  type="email"
                  value={buyerEmail}
                  onChange={e => setBuyerEmail(e.target.value)}
                  placeholder="Your email (for receipt & delivery)"
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-white/10 text-white placeholder-slate-500 outline-none focus:border-amber-400 text-sm"
                  data-testid="nft-buyer-email-input"
                />
                <button
                  onClick={buy}
                  disabled={buying || !buyerEmail.trim()}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950 font-black text-base flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-amber-500/30 transition-shadow disabled:opacity-40"
                  data-testid="nft-buy-btn"
                >
                  {buying ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  Buy with card via Crossmint {price ? `— ${price}` : ''}
                </button>
                {buyMsg && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/30 border border-amber-500/20" data-testid="nft-buy-message">
                    <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-200">{buyMsg}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-3 rounded-2xl bg-slate-800 text-slate-400 font-semibold text-sm" data-testid="nft-listing-inactive">
                This listing is no longer available.
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-6">
          Secure card payment &amp; NFT delivery by Crossmint · hibff.com
        </p>
      </div>
    </div>
  );
};

export default NFTListingPage;
